import type { ReactNode } from 'react'
import { ArrowLeft, LogOut } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { Link } from '@/components/link/Link'
import { kDefaultLogoPath } from '@/constants/constants-ui'
import { kLinkPrivacyPolicyUrl, kLinkTermsOfServiceUrl } from '@/constants/constants-links'

type CardPageLayoutProps = {
  /** Card content. On phone widths it sits directly on the page background, without the card. */
  children: ReactNode
  footer?: ReactNode
  /** Shows Sign Out: bottom-left on desktop, under the footer on phone widths. */
  onSignOut?: () => void
  /**
   * Shows Back beside the logo on phone widths only. On desktop the page places its own Back
   * button inside the card, next to its primary action.
   */
  onBack?: () => void
}

export function CardPageLayout({ children, footer, onSignOut, onBack }: CardPageLayoutProps) {
  return (
    <div className="bg-brand-neutral-gradient relative flex min-h-dvh flex-col">
      <header className="relative flex items-center justify-center px-4 pt-10 sm:pt-8">
        {onBack && (
          <div className="absolute left-4 sm:hidden">
            <Button variant="plain" leadingIcon={<ArrowLeft aria-hidden="true" />} onClick={onBack}>
              Back
            </Button>
          </div>
        )}
        <img className="h-9 w-auto sm:h-11" src={kDefaultLogoPath} alt="Paxton" />
      </header>

      <main className="flex flex-1 flex-col items-center justify-center px-4 py-8">
        <div className="w-full sm:w-[424px] sm:rounded-lg sm:border sm:border-brand-neutral-200 sm:bg-white sm:p-6 sm:shadow-[0px_0px_12px_0px_rgba(95,96,88,0.1)]">
          {children}
        </div>
      </main>

      {(footer || onSignOut) && (
        <footer className="flex flex-col items-center gap-6 px-4 pb-6">
          {footer}
          {onSignOut && (
            <div className="sm:absolute sm:bottom-5 sm:left-4">
              <Button variant="plain" leadingIcon={<LogOut aria-hidden="true" className="h-[18px] w-[18px]" />} onClick={onSignOut}>
                Sign Out
              </Button>
            </div>
          )}
        </footer>
      )}
    </div>
  )
}

export function CardPageLegalLinks() {
  return (
    <nav aria-label="Legal" className="flex items-center gap-6">
      <Link size="xs" href={kLinkTermsOfServiceUrl} external>
        Terms of Service
      </Link>
      <Link size="xs" href={kLinkPrivacyPolicyUrl} external>
        Privacy Policy
      </Link>
    </nav>
  )
}

export function CardPageAgreementNotice() {
  return (
    <p className="text-center text-xs font-medium text-brand-neutral-800">
      By creating an account, you agree to Paxton’s <br className="sm:hidden" />
      <Link size="xs" href={kLinkTermsOfServiceUrl} target="_blank" rel="noopener noreferrer">
        Terms of Service
      </Link>{' '}
      and acknowledge our{' '}
      <Link size="xs" href={kLinkPrivacyPolicyUrl} target="_blank" rel="noopener noreferrer">
        Privacy Policy
      </Link>
      .
    </p>
  )
}
